import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import date from "date-and-time";
import clsx from "clsx";
import { ColumnDef } from "@tanstack/react-table";
import {
  EyeIcon,
  PaperClipIcon,
  PlusIcon,
  SparklesIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

import { Button } from "../../components/base/Button";
import { Table } from "../../components/base/Table/Table";
import { BulkAction } from "../../components/base/Table/TableBulkActions";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCustomers from "../../data/Customer/useCustomers";
import useStaffUsers from "../../data/StaffUser/useStaffUsers";
import useTicketMutations from "../../data/Ticket/useTicketMutations";
import useTicketSummary from "../../data/Ticket/useTicketSummary";
import { TicketQueryKeys } from "../../data/Ticket/keys";
import {
  ITicket,
  TicketFeatureDecision,
  TicketPriority,
  TicketQuickFilter,
  TicketStatus,
  TicketType,
} from "../../models/Tickets/Ticket";
import TicketMergeModal from "./TicketMergeModal";
import TicketPeekModal from "./TicketPeekModal";
import TicketPriorityMenu from "./TicketPriorityMenu";
import TicketSelectCheckbox from "./TicketSelectCheckbox";
import TicketSummaryBar, { TicketSummarySelection } from "./TicketSummaryBar";
import {
  TicketFeatureDecisionPill,
  TicketStatusPill,
  TicketTypePill,
  ticketFeatureDecisionLabels,
  ticketFeatureDecisionOptions,
  ticketPriorityLabels,
  ticketPriorityOptions,
  ticketStatusLabels,
  ticketStatusOptions,
  ticketTypeLabels,
  ticketTypeOptions,
  pillBaseClasses,
} from "./ticketMeta";

/** Filter state lives in the URL so the list survives navigating to a ticket and back. */
interface TicketFilterState {
  customerId?: string;
  status?: TicketStatus;
  priority?: TicketPriority;
  type?: TicketType;
  /** Only ever set alongside type=Feature - setting it sets the type, and moving the type off Feature clears it. */
  featureDecision?: TicketFeatureDecision;
  assignedToUserId?: string;
  quickFilter?: TicketQuickFilter;
  includeResolved: boolean;
}

function parseFilters(params: URLSearchParams): TicketFilterState {
  const parseEnum = (value: string | null) =>
    value !== null && value !== "" ? Number(value) : undefined;

  return {
    customerId: params.get("customerId") ?? undefined,
    status: parseEnum(params.get("status")) as TicketStatus | undefined,
    priority: parseEnum(params.get("priority")) as TicketPriority | undefined,
    type: parseEnum(params.get("type")) as TicketType | undefined,
    featureDecision: parseEnum(params.get("featureDecision")) as
      | TicketFeatureDecision
      | undefined,
    assignedToUserId: params.get("assignedToUserId") ?? undefined,
    quickFilter: (params.get("quickFilter") as TicketQuickFilter) ?? undefined,
    includeResolved: params.get("includeResolved") === "true",
  };
}

export default function TicketsPage() {
  const { customers } = useCustomers();
  const { staffUsers } = useStaffUsers();
  const { setInfo } = usePageNameContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [peekTicketId, setPeekTicketId] = useState<string | null>(null);
  const [peekOpen, setPeekOpen] = useState(false);

  // Bulk actions run against the selection as it was when the action started,
  // so a refetch underneath an open modal can't change what gets acted on.
  const { bulkUpdateStatus, bulkAssignToMe } = useTicketMutations();
  const [bulkTickets, setBulkTickets] = useState<ITicket[]>([]);
  const clearSelectionRef = useRef<() => void>(() => {});
  const [mergeOpen, setMergeOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<
    TicketStatus.Closed | TicketStatus.Resolved | null
  >(null);

  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);

  const { summary } = useTicketSummary({ customerId: filters.customerId });

  useEffect(() => {
    setInfo({
      name: "Tickets",
      desc: "View and manage support tickets.",
      pageTitle: "Tickets",
    });
  }, [setInfo]);

  const updateFilters = (changes: Partial<TicketFilterState>) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(changes).forEach(([key, value]) => {
      if (value === undefined || value === "" || value === false) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });

    setSearchParams(next, { replace: true });
  };

  const handleSummarySelect = (selection: TicketSummarySelection) => {
    // Selecting a Resolved/Closed tile has to lift the active-only default,
    // otherwise the table filters away the very rows the tile counted.
    const showsClosed =
      selection.status === TicketStatus.Resolved ||
      selection.status === TicketStatus.Closed ||
      selection.quickFilter === "ClosedToday" ||
      selection.quickFilter === "ClosedThisWeek";

    updateFilters({
      ...selection,
      ...(showsClosed ? { includeResolved: true } : {}),
    });
  };

  const getCustomerName = useCallback(
    (customerId: string) =>
      customers.data?.find((x) => x.id === customerId)?.name ?? customerId,
    [customers.data],
  );

  const getStaffName = useCallback(
    (email?: string) => {
      if (!email) return "Unassigned";
      const staff = staffUsers.data?.find(
        (x) => x.email?.toLowerCase() === email.toLowerCase(),
      );
      return staff?.displayName || email;
    },
    [staffUsers.data],
  );

  const openTicketInNewTab = (ticketId: string) =>
    window.open(`/tickets/${ticketId}`, "_blank", "noopener,noreferrer");

  const columns = useMemo<ColumnDef<ITicket, any>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <TicketSelectCheckbox
            label="Select all tickets on this page"
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <TicketSelectCheckbox
            label={`Select ${row.original.subject}`}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      },
      {
        header: "Subject",
        accessorKey: "subject",
        meta: { sortKey: "subject" },
        // Truncated rather than wrapped so long subjects can't widen the table
        // past the page; the full text is in the tooltip.
        cell: ({ row }) => (
          <a
            href={`/tickets/${row.original.id}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={row.original.subject}
            className="block max-w-[20rem] truncate text-slate-900 hover:underline"
          >
            {row.original.subject}
          </a>
        ),
      },
      {
        header: "Customer",
        accessorKey: "customerId",
        meta: { sortKey: "customer" },
        cell: ({ row }) => {
          const name = getCustomerName(row.original.customerId);
          return (
            <span className="block max-w-[12rem] truncate" title={name}>
              {name}
            </span>
          );
        },
      },
      {
        header: "Type",
        accessorKey: "type",
        meta: { sortKey: "type" },
        cell: ({ row }) => <TicketTypePill type={row.original.type} />,
      },
      {
        header: "Priority",
        accessorKey: "priority",
        meta: { sortKey: "priority" },
        // Feature requests carry their internal decision alongside the priority.
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <TicketPriorityMenu
              ticketId={row.original.id}
              priority={row.original.priority}
            />
            <TicketFeatureDecisionPill
              type={row.original.type}
              decision={row.original.featureDecision}
            />
          </div>
        ),
      },
      {
        header: "Status",
        accessorKey: "status",
        meta: { sortKey: "status" },
        // Reply state sits alongside the status so a row's state is readable at a glance.
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <TicketStatusPill status={row.original.status} />
            {!row.original.hasStaffReply ? (
              <span
                className={clsx(pillBaseClasses, "bg-rose-100 text-rose-700")}
                title="Nobody on staff has replied yet"
              >
                No reply
              </span>
            ) : null}
            {row.original.awaitingResponse ? (
              <span
                className={clsx(
                  pillBaseClasses,
                  "bg-yellow-100 text-yellow-800",
                )}
                title="The customer replied last"
              >
                Awaiting Response
              </span>
            ) : null}
          </div>
        ),
      },
      {
        header: () => (
          <UserIcon className="h-4 w-4" aria-label="Customer replies" />
        ),
        id: "customerNotes",
        cell: ({ row }) => (
          <span
            className="text-sm text-gray-600"
            title={`${row.original.customerNoteCount} customer reply(s)`}
          >
            {row.original.customerNoteCount}
          </span>
        ),
      },
      {
        header: () => (
          <PaperClipIcon className="h-4 w-4" aria-label="Attachments" />
        ),
        id: "attachments",
        cell: ({ row }) => (
          <span
            className="text-sm text-gray-600"
            title={`${row.original.attachments?.length ?? 0} attachment(s)`}
          >
            {row.original.attachments?.length ?? 0}
          </span>
        ),
      },
      {
        header: "Assignee",
        accessorKey: "assignedToUserId",
        meta: { sortKey: "assignedto" },
        cell: ({ row }) => (
          <span
            title={row.original.assignedToUserId ?? "Unassigned"}
            className={clsx(
              "block max-w-[10rem] truncate",
              row.original.assignedToUserId ? "" : "italic text-gray-500",
            )}
          >
            {getStaffName(row.original.assignedToUserId)}
          </span>
        ),
      },
      {
        header: "Created",
        accessorKey: "created",
        meta: { sortKey: "created" },
        cell: ({ row }) =>
          date.format(new Date(row.original.created), "DD/MM/YY HH:mm"),
      },
      {
        header: "Actions",
        id: "actions",
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setPeekTicketId(row.original.id);
              setPeekOpen(true);
            }}
            className="inline-flex items-center gap-1 text-orange-700 hover:text-orange-900"
          >
            <EyeIcon className="h-4 w-4" />
            Peek
          </button>
        ),
      },
    ],
    [getCustomerName, getStaffName],
  );

  // Only defined values are sent; the Table spreads these onto the query string.
  const externalFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries({
          customerId: filters.customerId,
          status: filters.status,
          priority: filters.priority,
          type: filters.type,
          featureDecision: filters.featureDecision,
          assignedToUserId: filters.assignedToUserId,
          quickFilter: filters.quickFilter,
          includeResolved: filters.includeResolved || undefined,
          includeNotes: false,
        }).filter(([, value]) => value !== undefined),
      ),
    [filters],
  );

  const summarySelection: TicketSummarySelection = {
    status: filters.status,
    priority: filters.priority,
    quickFilter: filters.quickFilter,
    assignedToUserId: filters.assignedToUserId,
  };

  const startBulkAction = (rows: ITicket[], clearSelection: () => void) => {
    setBulkTickets(rows);
    clearSelectionRef.current = clearSelection;
  };

  const bulkActions: BulkAction[] = [
    {
      label: "Merge",
      onClick: (rows: ITicket[], clearSelection) => {
        startBulkAction(rows, clearSelection);
        setMergeOpen(true);
      },
    },
    {
      label: "Close",
      onClick: (rows: ITicket[], clearSelection) => {
        startBulkAction(rows, clearSelection);
        setBulkStatus(TicketStatus.Closed);
      },
    },
    {
      label: "Resolve",
      onClick: (rows: ITicket[], clearSelection) => {
        startBulkAction(rows, clearSelection);
        setBulkStatus(TicketStatus.Resolved);
      },
    },
    {
      label: "Assign to me",
      onClick: (rows: ITicket[], clearSelection) =>
        bulkAssignToMe.mutate(
          rows.map((x) => x.id),
          { onSuccess: () => clearSelection() },
        ),
    },
  ];

  const handleBulkStatus = () => {
    if (bulkStatus === null) return;

    bulkUpdateStatus.mutate(
      { ticketIds: bulkTickets.map((x) => x.id), status: bulkStatus },
      {
        onSuccess: () => {
          clearSelectionRef.current();
          setBulkStatus(null);
        },
      },
    );
  };

  const bulkStatusVerb =
    bulkStatus === TicketStatus.Closed ? "Close" : "Resolve";

  const selectClasses = "rounded border border-gray-300 px-3 py-2 text-sm";

  const toolbar = (
    <TicketSummaryBar
      summary={summary.data}
      isLoading={summary.isLoading}
      selection={summarySelection}
      customerName={
        filters.customerId ? getCustomerName(filters.customerId) : undefined
      }
      staffUsers={staffUsers.data}
      onSelect={handleSummarySelect}
    />
  );

  // Rendered in the same row as the table's search bar.
  const inlineFilters = (
    <>
      <div>
        <label
          htmlFor="ticket-customer-filter"
          className="mb-1 block text-sm font-medium text-gray-700"
        >
          Customer
        </label>
        <select
          id="ticket-customer-filter"
          className={`${selectClasses} w-64`}
          value={filters.customerId ?? ""}
          onChange={(e) => updateFilters({ customerId: e.target.value })}
        >
          <option value="">All customers</option>
          {(customers.data ?? []).map((customer) => (
            <option key={customer.id} value={customer.id}>
              {customer.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Status
        </label>
        <select
          className={selectClasses}
          value={filters.status ?? ""}
          onChange={(e) =>
            handleSummarySelect({
              status:
                e.target.value === ""
                  ? undefined
                  : (Number(e.target.value) as TicketStatus),
            })
          }
        >
          <option value="">Any</option>
          {ticketStatusOptions.map((status) => (
            <option key={status} value={status}>
              {ticketStatusLabels[status]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Priority
        </label>
        <select
          className={selectClasses}
          value={filters.priority ?? ""}
          onChange={(e) =>
            updateFilters({
              priority:
                e.target.value === ""
                  ? undefined
                  : (Number(e.target.value) as TicketPriority),
            })
          }
        >
          <option value="">Any</option>
          {ticketPriorityOptions.map((priority) => (
            <option key={priority} value={priority}>
              {ticketPriorityLabels[priority]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Decision
        </label>
        <select
          className={selectClasses}
          value={filters.featureDecision ?? ""}
          onChange={(e) => {
            const featureDecision =
              e.target.value === ""
                ? undefined
                : (Number(e.target.value) as TicketFeatureDecision);

            // Bugs default to Undecided too, so a decision filter narrows to
            // features - otherwise "Undecided" would pull in every bug.
            updateFilters({
              featureDecision,
              ...(featureDecision !== undefined
                ? { type: TicketType.Feature }
                : {}),
            });
          }}
        >
          <option value="">Any</option>
          {ticketFeatureDecisionOptions.map((decision) => (
            <option key={decision} value={decision}>
              {ticketFeatureDecisionLabels[decision]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Type
        </label>
        <select
          className={selectClasses}
          value={filters.type ?? ""}
          onChange={(e) => {
            const type =
              e.target.value === ""
                ? undefined
                : (Number(e.target.value) as TicketType);

            // A decision only applies to features, so moving the type off
            // Feature drops the decision filter with it.
            updateFilters({
              type,
              ...(type === TicketType.Feature
                ? {}
                : { featureDecision: undefined }),
            });
          }}
        >
          <option value="">Any</option>
          {ticketTypeOptions.map((type) => (
            <option key={type} value={type}>
              {ticketTypeLabels[type]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Assignee
        </label>
        <select
          className={`${selectClasses} w-56`}
          value={filters.assignedToUserId ?? ""}
          onChange={(e) => updateFilters({ assignedToUserId: e.target.value })}
        >
          <option value="">Anyone</option>
          {(staffUsers.data ?? []).map((staff) => (
            <option key={staff.userId} value={staff.email}>
              {staff.displayName || staff.email}
            </option>
          ))}
        </select>
      </div>

      {/* The controls below have no label of their own, so they sit on the same
          baseline as the labelled selects rather than riding higher. */}
      <label className="flex h-[38px] items-center gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={filters.includeResolved}
          onChange={(e) => updateFilters({ includeResolved: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
        />
        Show All
      </label>

      <Button
        variant="outlined"
        className="h-[38px]"
        onClick={() =>
          setSearchParams(new URLSearchParams(), { replace: true })
        }
      >
        Clear filters
      </Button>
    </>
  );

  return (
    <div className="space-y-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Link to="/tickets/new">
          <Button className="flex items-center space-x-2">
            <PlusIcon className="-ml-1 h-5 w-5" />
            <p>Create Ticket</p>
          </Button>
        </Link>
        <Link
          to={
            filters.customerId
              ? `/tickets/insights?customerId=${filters.customerId}`
              : "/tickets/insights"
          }
        >
          <Button variant="outlined" className="flex items-center space-x-2">
            <SparklesIcon className="-ml-1 h-5 w-5" />
            <p>AI Insights</p>
          </Button>
        </Link>
      </div>

      <Table<ITicket>
        url="/ticket"
        queryKey={TicketQueryKeys.all()}
        columns={columns}
        externalFilters={externalFilters}
        searchLabel="Search tickets"
        sortable
        defaultSort={{ sortBy: "created", sortDescending: true }}
        onRowClick={(ticket) => openTicketInNewTab(ticket.id)}
        toolbar={toolbar}
        inlineFilters={inlineFilters}
        hideTotalCount
        bulkActions={bulkActions}
        bulkActionsLabel="Bulk Actions"
      />

      <TicketMergeModal
        tickets={bulkTickets}
        open={mergeOpen}
        setOpen={setMergeOpen}
        getCustomerName={getCustomerName}
        getStaffName={getStaffName}
        onMerged={() => clearSelectionRef.current()}
      />

      <ConfirmModal
        open={bulkStatus !== null}
        setOpen={(open: boolean) => !open && setBulkStatus(null)}
        title={`${bulkStatusVerb} ${bulkTickets.length} ticket${bulkTickets.length === 1 ? "" : "s"}?`}
        message={`They'll be marked ${bulkStatusVerb === "Close" ? "closed" : "resolved"} and drop out of the active list. Nobody is emailed.`}
        confirmButtonText={bulkStatusVerb}
        onConfirm={handleBulkStatus}
        isLoading={bulkUpdateStatus.isPending}
      />

      <TicketPeekModal
        ticketId={peekTicketId}
        open={peekOpen}
        setOpen={setPeekOpen}
      />
    </div>
  );
}
